/**
 * useProducts — Products data hook + shared optimistic store.
 *
 * Products are an external catalog (imported via a "Product imports"
 * integration) that can be attached to events, appointment services and queue
 * services. A product may be associated with MANY services/events, and a
 * service/event may carry MANY products (many-to-many, tracked here as
 * `serviceIds`/`eventIds` on each product).
 *
 * DATA SOURCE (drop-32 gate cut-over, 2026-07-11): the server IS the catalog —
 * `listProducts` hydrates the in-memory cache (rows REPLACE it; the
 * localStorage interim is retired). Association writes persist through the
 * four idempotent attach/detach ops. Product AUTHORING (create/edit/delete)
 * has NO ops yet — declared REQ-027 — so the screens disable those
 * affordances via productAuthoringAvailable() instead of faking success
 * (no-fake-interactivity mandate). NO seed rows are authored here; the demo
 * catalog lives in @v0y4ge/api-mocks behind listProducts (REQ-024, closed).
 */
import { useEffect, useState } from "react";
import * as apiClient from "@v0y4ge/api-client";
import { logUpsert, logDelete } from "../../lib/audit";
import { can, onTenantChange, onTenantSwitching } from "../../shell/access";

const api = apiClient as Record<string, unknown>;

export type ProductStatus = "active" | "inactive";
export interface Product {
  id: string;
  name: string;
  sku: string;
  category: string;
  price: number;          // in major currency units; 0 => "Free"
  currency: string;       // ISO code, e.g. "USD"
  status: ProductStatus;
  vendor?: string;        // external source / supplier
  imageUrl?: string;
  description?: string;
  serviceIds: string[];   // associated appointment/queue service ids
  eventIds: string[];     // associated event ids
  externalId?: string;    // id in the source system
  updated?: string;
}

export const PRODUCT_CATEGORIES = ["Merchandise", "Add-on", "Food & beverage", "Equipment", "Digital", "Bundle"] as const;

/* ── Hydration — the catalog is the import feed's, not CD's ──────────────
 * Server rows REPLACE the in-memory cache. No localStorage (retired per the
 * drop-32 gate docket); without the op (older preview stub) the catalog is an
 * honest empty state. */
const LS_RETIRED = "jrni.products.v1"; // key of the retired interim store (left for reference only; never read)
void LS_RETIRED;
let _cache: Product[] | null = null;
let _hydrated = false;
let _loading = false;
const _subs = new Set<() => void>();

function _hydrate() {
  if (_hydrated) return;
  _hydrated = true;
  const fn = api.listProducts;
  if (typeof fn !== "function") return; // op absent (stub preview) — honest empty
  _loading = true;
  Promise.resolve((fn as () => Promise<unknown>)()).then((res) => {
    const r = res as { products?: Product[]; items?: Product[]; data?: Product[] } | Product[] | null;
    const rows = Array.isArray(r) ? r : (r?.products ?? r?.items ?? r?.data);
    if (Array.isArray(rows)) _cache = rows.filter(Boolean) as Product[];
  }).catch((e) => { console.warn("listProducts failed:", e); })
    .finally(() => { _loading = false; _emit(); });
}

function _load(): Product[] {
  if (!_cache) _cache = [];
  _hydrate();
  return _cache;
}
function _emit() { _subs.forEach((f) => { try { f(); } catch { /* noop */ } }); }
onTenantSwitching(() => { _cache = null; _hydrated = false; _loading = false; _emit(); });
// Capability gate (docket 2026-07-31) — see services-data.ts for the shape.
// onTenantSwitching above already cleared _hydrated, so ProductsStore.all()
// (route-guarded on products.read) still hydrates on demand for a persona that
// actually opens the catalog.
onTenantChange(() => { if (can("products.read")) _hydrate(); });

/** True when the product AUTHORING ops exist (createProduct/updateProduct —
 * declared REQ-027). Until then the catalog is read-only in Studio (it belongs
 * to the import feed): New/Edit-fields/Duplicate/status/Remove affordances are
 * disabled rather than faking persistence. Associations are unaffected (their
 * ops are live). */
export function productAuthoringAvailable(): boolean {
  return typeof api.createProduct === "function" && typeof api.updateProduct === "function";
}

// Store of METHODS (all/get/newId/create-through-real-op), not a data row;
// authoring is disabled rather than faked when the real op is absent (REQ-027).
// Marked per Claude Code docket 2026-07-13 (seed-guard false-positive on the
// declaration-line heuristic).
// ui-config
export const ProductsStore = {
  all: (): Product[] => _load(),
  loading: () => _loading,
  get: (id: string): Product | undefined => _load().find((p) => p.id === id),
  newId: () => `prd_${Date.now().toString(36)}`,
  /** Create/update through the REAL authoring op only (REQ-027). Returns null
   * — nothing persisted, nothing cached — when the ops don't exist yet;
   * callers keep those affordances disabled via productAuthoringAvailable(). */
  async upsert(p: Product): Promise<Product | null> {
    const list = _load();
    const i = list.findIndex((x) => x.id === p.id);
    const op = i >= 0 ? api.updateProduct : api.createProduct;
    if (typeof op !== "function") { console.warn("product authoring ops absent (REQ-027) — nothing saved"); return null; }
    const res = await (i >= 0
      ? (op as (id: string, input: Product) => Promise<{ product?: Product }>)(p.id, p)
      : (op as (input: Product) => Promise<{ product?: Product }>)(p)) as { product?: Product };
    const saved = (res?.product ?? { ...p, updated: "Just now" }) as Product;
    logUpsert("product", i >= 0 ? (list[i] as unknown as Record<string, unknown>) : null, saved as unknown as Record<string, unknown>, { name: saved.name });
    _cache = i >= 0 ? list.map((x) => (x.id === p.id ? saved : x)) : [saved, ...list];
    _emit();
    return saved;
  },
  async remove(id: string): Promise<boolean> {
    const del = api.deleteProduct;
    if (typeof del !== "function") { console.warn("deleteProduct absent (REQ-027) — nothing removed"); return false; }
    const p = _load().find((x) => x.id === id);
    await (del as (id: string) => Promise<unknown>)(id);
    if (p) logDelete("product", p as unknown as Record<string, unknown>, { name: p.name });
    _cache = _load().filter((x) => x.id !== id);
    _emit();
    return true;
  },
  async setStatus(id: string, status: ProductStatus): Promise<boolean> {
    const p = ProductsStore.get(id);
    if (!p) return false;
    return !!(await ProductsStore.upsert({ ...p, status }));
  },
  async duplicate(id: string): Promise<Product | null> {
    const p = ProductsStore.get(id); if (!p) return null;
    return ProductsStore.upsert({ ...p, id: ProductsStore.newId(), name: `${p.name} (copy)`, sku: `${p.sku}-COPY`, status: "inactive", serviceIds: [], eventIds: [] });
  },
  /** Product-side association save (the editor's Attach-to pickers): diff the
   * stored ids against `next` and fire one idempotent attach/detach op per
   * change. Errors PROPAGATE so the editor renders a real failure state. */
  async setProductAssociations(productId: string, next: { eventIds: string[]; serviceIds: string[] }): Promise<void> {
    if (!_assocOp("attachProductToEvent")) {
      // Stub preview without the ops — reflect in memory (declared interim,
      // design-preview only; a live tenant always resolves the attach ops).
      // §2.10 open question answered 2026-07-26: intended scope IS preview-only;
      // flagged loudly so a preview save is never mistaken for a persisted one.
      console.info("[preview] product associations reflected in-memory only — attach ops absent (not persisted)");
      _cache = _load().map((x) => (x.id === productId ? { ...x, eventIds: [...next.eventIds], serviceIds: [...next.serviceIds] } : x));
      _emit();
      return;
    }
    const cur = ProductsStore.get(productId);
    const plan: Array<[string, Record<string, string>]> = [];
    const diff = (curIds: string[], nextIds: string[], attach: string, detach: string, key: string) => {
      const c = new Set(curIds), n = new Set(nextIds);
      nextIds.forEach((v) => { if (!c.has(v)) plan.push([attach, { productId, [key]: v }]); });
      curIds.forEach((v) => { if (!n.has(v)) plan.push([detach, { productId, [key]: v }]); });
    };
    diff(cur?.eventIds ?? [], next.eventIds, "attachProductToEvent", "detachProductFromEvent", "eventId");
    diff(cur?.serviceIds ?? [], next.serviceIds, "attachProductToService", "detachProductFromService", "serviceId");
    for (const [name, input] of plan) {
      // §2.10 (2026-07-26): a resolved {ok:false} previously slid through and
      // the editor toasted success — require ok!==false AND a returned product
      // row before applying; anything else throws to the editor's failure path.
      const res = (await _assocOp(name)!(input)) as { ok?: boolean; product?: Product };
      if (res?.ok === false || !res?.product) throw new Error(`${name} did not persist (ok=${String(res?.ok)})`);
      _applyProduct(res.product);
    }
  },
  /* ── Association helpers (many-to-many, driven from either side) ───────
   * drop29 (REQ-024 live): associations persist through the four idempotent
   * attach/detach ops — the methods DIFF current vs next ids and fire one call
   * per changed id, updating the cache from each op's returned product row.
   * When the ops are absent (older preview stub) the local write remains the
   * declared interim behavior. */
  productsForService: (serviceId: string): Product[] => _load().filter((p) => p.serviceIds.includes(serviceId)),
  productsForEvent: (eventId: string): Product[] => _load().filter((p) => p.eventIds.includes(eventId)),
  async setServiceProducts(serviceId: string, productIds: string[]) {
    const attach = _assocOp("attachProductToService");
    const detach = _assocOp("detachProductFromService");
    if (attach && detach) {
      const cur = _load().filter((p) => p.serviceIds.includes(serviceId)).map((p) => p.id);
      const next = new Set(productIds);
      const curSet = new Set(cur);
      try {
        for (const id of productIds) if (!curSet.has(id)) _applyProduct((await attach({ productId: id, serviceId })).product);
        for (const id of cur) if (!next.has(id)) _applyProduct((await detach({ productId: id, serviceId })).product);
      } catch (e) { console.warn("product↔service association write failed:", e); }
      return;
    }
    _cache = _load().map((p) => {
      const has = p.serviceIds.includes(serviceId);
      const should = productIds.includes(p.id);
      if (has === should) return p;
      return { ...p, serviceIds: should ? [...p.serviceIds, serviceId] : p.serviceIds.filter((s) => s !== serviceId) };
    });
    _emit();
  },
  async setEventProducts(eventId: string, productIds: string[]) {
    const attach = _assocOp("attachProductToEvent");
    const detach = _assocOp("detachProductFromEvent");
    if (attach && detach) {
      const cur = _load().filter((p) => p.eventIds.includes(eventId)).map((p) => p.id);
      const next = new Set(productIds);
      const curSet = new Set(cur);
      try {
        for (const id of productIds) if (!curSet.has(id)) _applyProduct((await attach({ productId: id, eventId })).product);
        for (const id of cur) if (!next.has(id)) _applyProduct((await detach({ productId: id, eventId })).product);
      } catch (e) { console.warn("product↔event association write failed:", e); }
      return;
    }
    _cache = _load().map((p) => {
      const has = p.eventIds.includes(eventId);
      const should = productIds.includes(p.id);
      if (has === should) return p;
      return { ...p, eventIds: should ? [...p.eventIds, eventId] : p.eventIds.filter((e) => e !== eventId) };
    });
    _emit();
  },
};

type AssocFn = (input: Record<string, string>) => Promise<{ ok?: boolean; product?: Product }>;
function _assocOp(name: string): AssocFn | null {
  const f = api[name] ?? (api.products as Record<string, unknown> | undefined)?.[name];
  return typeof f === "function" ? (f as AssocFn) : null;
}
/** Merge an op-returned product row (the association arrays' source of truth). */
function _applyProduct(p?: Product | null): void {
  if (!p || !p.id) return;
  _cache = _load().map((x) => (x.id === p.id ? { ...x, ...p } : x));
  _emit();
}

export function useProducts() {
  const [, force] = useState(0);
  useEffect(() => { const cb = () => force((n) => n + 1); _subs.add(cb); return () => { _subs.delete(cb); }; }, []);
  return {
    products: ProductsStore.all(),
    loading: ProductsStore.loading(),
    authoringAvailable: productAuthoringAvailable(),
    get: ProductsStore.get,
    upsert: ProductsStore.upsert,
    remove: ProductsStore.remove,
    setStatus: ProductsStore.setStatus,
    duplicate: ProductsStore.duplicate,
    productsForService: ProductsStore.productsForService,
    productsForEvent: ProductsStore.productsForEvent,
    setServiceProducts: ProductsStore.setServiceProducts,
    setEventProducts: ProductsStore.setEventProducts,
    setProductAssociations: ProductsStore.setProductAssociations,
  };
}

export function productPriceLabel(p: Product): string {
  if (!p.price || p.price === 0) return "Free";
  try { return new Intl.NumberFormat(undefined, { style: "currency", currency: p.currency || "USD", maximumFractionDigits: 2 }).format(p.price); }
  catch { return `$${p.price}`; }
}
